import { z } from 'zod';

/**
 * Integration sync status codes (mirror of the API's SYNC_STATUS enum).
 *
 * AUTH_FAILED is terminal — the integration has enabled=false and the
 * user must reconnect via OAuth before it can sync again. The UI uses
 * this to swap the "Enable" switch for a "Reconnect" button.
 */
export const SYNC_STATUS = {
  IDLE: 0,
  SYNCING: 1,
  FAILED: 2,
  AUTH_FAILED: 3,
} as const;

export type SyncStatusCode = (typeof SYNC_STATUS)[keyof typeof SYNC_STATUS];

/**
 * Integration response schema.
 *
 * `credentials` contains non-sensitive metadata only (e.g., email, username for display).
 * Sensitive fields (passwords, tokens) are encrypted server-side and never returned to the client.
 */
export const integrationResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  source: z.string(),
  credentials: z.record(z.string(), z.unknown()), // Non-sensitive only (email, username)
  config: z.record(z.string(), z.unknown()),
  lastSyncAt: z.string().nullable(),
  lastSyncCursor: z.string().nullable(),
  syncStatus: z.number(),
  syncError: z.string().nullable(),
  // Registered EHR error code for syncError, read from the latest failed run
  // (ADR-253; not a column). Show the standard message with `ehrErrorMessage`.
  syncErrorCode: z.string().nullable().optional(),
  enabled: z.boolean(),
  rowStatus: z.number(),
  createdBy: z.string().uuid().nullable(),
  updatedBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type IntegrationResponse = z.infer<typeof integrationResponseSchema>;

/**
 * Create integration request schema.
 */
export const createIntegrationSchema = z.object({
  source: z.string().min(1, 'Integration source is required'),
  credentials: z.record(z.string(), z.unknown()).optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  enabled: z.boolean().optional(),
});

export type CreateIntegrationRequest = z.infer<typeof createIntegrationSchema>;

/**
 * Update integration request schema.
 */
export const updateIntegrationSchema = z.object({
  credentials: z.record(z.string(), z.unknown()).optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  enabled: z.boolean().optional(),
});

export type UpdateIntegrationRequest = z.infer<typeof updateIntegrationSchema>;

/**
 * Enable/disable an integration (`PATCH /api/integrations/:id/toggle`).
 */
export const toggleIntegrationSchema = z.object({
  enabled: z.boolean(),
});

export type ToggleIntegrationRequest = z.infer<typeof toggleIntegrationSchema>;

/**
 * Sync options schema.
 */
export const syncOptionsSchema = z.object({
  limit: z.number().int().positive().optional(),
  syncPatients: z.boolean().optional(),
  syncProviders: z.boolean().optional(),
  syncAppointments: z.boolean().optional(),
  syncSlots: z.boolean().optional(),
  timezone: z.string().optional(),
});

export type SyncOptions = z.infer<typeof syncOptionsSchema>;

/**
 * Sync result schema.
 */
export const syncResultSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});

export type SyncResult = z.infer<typeof syncResultSchema>;

/**
 * What refreshing one patient from the EHR (patient detail → Sync EHR) stored.
 * A refresh succeeds once the patient itself is read, so `errors` is how the
 * caller learns that part of the patient's data may not have been stored.
 */
export const patientSyncResultSchema = z.object({
  /**
   * Appointment rows the refresh upserted, cancelled and past ones included.
   * Counts every row stored, including any that could not be linked to the
   * patient, so it is not the size of the patient's Appointments tab.
   */
  appointments: z.number().int().nonnegative(),
  /**
   * Problems the sync reported. Not a count of dropped records: one failed read
   * (a department or page of appointments) can cost many records, and some
   * (an insurance read) drop nothing.
   */
  errors: z.number().int().nonnegative(),
});

export type PatientSyncResult = z.infer<typeof patientSyncResultSchema>;

/**
 * Catalog import — the selective "pull from EHR" flow. Distinct from streaming
 * sync: the user picks which catalog objects to pull and each is upserted by its
 * EHR id (merge semantics — admin edits are preserved).
 */
export const CATALOG_IMPORT_OBJECTS = [
  'providers',
  'locations',
  'appointmentTypes',
  'appointmentReasons',
  'slots',
] as const;

export const catalogImportObjectSchema = z.enum(CATALOG_IMPORT_OBJECTS);

export type CatalogImportObject = z.infer<typeof catalogImportObjectSchema>;

/**
 * What the Integrations page's athena **Sync** button imports: the catalog only,
 * never `slots`. On 2026-09-29 one press with `slots` made 3,602 athena
 * `GET /appointments/open` calls in 16 minutes (one per department × appointment
 * type), drew a 429 from athena and timed the request out (504 at 300 s). The API
 * now starts the worker's gated slot refresh after a locations or appointment-type
 * import, and `slots` does the same instead of reading slots inline. Step 1 of
 * ADR-299 (docs/athena-schedule-feed.md).
 */
export const CATALOG_SYNC_OBJECTS = [
  'providers',
  'locations',
  'appointmentTypes',
  'appointmentReasons',
] as const satisfies readonly CatalogImportObject[];

/**
 * Integration sources whose connector implements catalog import. The "Pull from
 * EHR" UI gates on this so the action only appears for sources the backend can
 * actually serve (the backend independently rejects unsupported sources via
 * `supportsCatalogImport`). Add a source here when its connector gains the
 * `CatalogImportConnector` capability.
 */
export const CATALOG_IMPORT_SOURCES: readonly string[] = ['athena'];

export const catalogImportRequestSchema = z.object({
  objects: z
    .array(catalogImportObjectSchema)
    .min(1, 'Select at least one object to import')
    // de-dupe so callers can't inflate work by repeating an object
    .transform((objects) => [...new Set(objects)]),
});

export type CatalogImportRequest = z.infer<typeof catalogImportRequestSchema>;

/** Per-object outcome: how many were fetched from the EHR, upserted, and skipped on error. */
export const catalogImportCountSchema = z.object({
  fetched: z.number(),
  upserted: z.number(),
  failed: z.number(),
});

export type CatalogImportCount = z.infer<typeof catalogImportCountSchema>;

export const catalogImportFailureSchema = z.object({
  object: z.enum(CATALOG_IMPORT_OBJECTS),
  errorCode: z.string(),
  message: z.string(),
});

export type CatalogImportFailure = z.infer<typeof catalogImportFailureSchema>;

export const catalogImportResultSchema = z.object({
  providers: catalogImportCountSchema.optional(),
  locations: catalogImportCountSchema.optional(),
  /**
   * Provider-location links the EHR reported on the imported locations
   * (ADR-261): `fetched` pairs reported, `upserted` links added (existing ones
   * are left as they are), `failed` pairs whose provider or location isn't in
   * VagusX. Present whenever locations were read.
   */
  providerLocations: catalogImportCountSchema.optional(),
  appointmentTypes: catalogImportCountSchema.optional(),
  appointmentReasons: catalogImportCountSchema.optional(),
  slots: catalogImportCountSchema.optional(),
  /** Sampled human-readable errors encountered during the import (capped). */
  errors: z.array(z.string()),
  /**
   * Objects that could not be read from the EHR at all, each with its
   * registered EHR error code (ADR-255). Show the standard message with
   * `ehrErrorMessage(errorCode, message)`. Absent when every object was read.
   */
  failures: z.array(catalogImportFailureSchema).optional(),
});

export type CatalogImportResult = z.infer<typeof catalogImportResultSchema>;

/**
 * Test credentials request schema.
 *
 * `credentials` carries auth fields (username/password/tokens); `config`
 * carries non-secret tenant identifiers (officeId/practiceId). The split
 * mirrors how the integration row stores them after save:
 *   - OA: credentials = {username, password}, config = {}
 *   - athena: credentials = {}, config = {practiceId}
 *
 * The connector for each source owns the shape contract — this transport layer
 * just forwards both records.
 */
export const testCredentialsRequestSchema = z.object({
  source: z.string().min(1, 'Integration source is required'),
  credentials: z.record(z.string(), z.string()),
  config: z.record(z.string(), z.string()).optional(),
});

export type TestCredentialsRequest = z.infer<typeof testCredentialsRequestSchema>;

/**
 * Test credentials result schema.
 */
export const testCredentialsResultSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  /** The account the credentials reached (athena: the practice name), when the connector can name it (ADR-260). */
  accountName: z.string().optional(),
});

export type TestCredentialsResult = z.infer<typeof testCredentialsResultSchema>;

/**
 * OAuth authorize response schema.
 */
export const oauthAuthorizeResponseSchema = z.object({
  url: z.string(),
});

export type OAuthAuthorizeResponse = z.infer<typeof oauthAuthorizeResponseSchema>;

/**
 * Gmail history sync state, stored in integration config.historySync.
 * Shared between the Gmail service and the frontend.
 */
export const historySyncStateSchema = z.object({
  status: z.enum(['pending', 'running', 'completed', 'failed']),
  query: z.string(),
  pageToken: z.string().nullable(),
  anchorHistoryId: z.string(),
  processed: z.number(),
  inserted: z.number(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  error: z.string().nullable(),
  lastCheckpointAt: z.string(),
  lastProcessedDate: z.string().nullable().optional(),
});

export type HistorySyncState = z.infer<typeof historySyncStateSchema>;

/**
 * One recorded EHR API call (ADR-095) — observability for the connector-activity
 * panel. PHI-free: the connector redacts params before recording. Newest-first
 * when returned as a list.
 */
export const ehrActivityCallSchema = z.object({
  seq: z.number(),
  method: z.string(),
  /** The real EHR host this call went to, e.g. `https://api.preview.platform.athenahealth.com`. */
  host: z.string().optional(),
  path: z.string(),
  query: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  form: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  status: z.number().nullable(),
  durationMs: z.number(),
  outcome: z.enum(['success', 'error']),
  error: z.string().optional(),
  startedAt: z.string(),
  /** PHI-redacted, truncated response body, when the connector captured one. */
  responseBody: z.unknown().optional(),
});

export type EhrActivityCall = z.infer<typeof ehrActivityCallSchema>;

/**
 * Recent-EHR-activity response. `source` is the org's EHR integration source (or
 * null when it has none); `supported` is false when that connector cannot report
 * its call activity (e.g. a scraping connector), so the UI can hide the panel.
 */
export const ehrActivityResponseSchema = z.object({
  source: z.string().nullable(),
  supported: z.boolean(),
  calls: z.array(ehrActivityCallSchema),
});

export type EhrActivityResponse = z.infer<typeof ehrActivityResponseSchema>;

// =============================================================================
// Sync streams and change-feed subscriptions (ADR-299)
// =============================================================================

/**
 * Every sync an integration can run (ADR-299 §4). Only the ones the API returns
 * for an integration can be run or paused there.
 */
export const INTEGRATION_STREAM_NAMES = [
  'catalog',
  'schedule_load',
  'schedule_edge',
  'appointment_feed',
  'patient_feed',
  'waitlist_push',
  'waitlist_pull',
] as const;

export const integrationStreamNameSchema = z.enum(INTEGRATION_STREAM_NAMES);
export type IntegrationStreamName = z.infer<typeof integrationStreamNameSchema>;

/** A stream's status, derived by the API (never stored). */
export const STREAM_STATUSES = ['running', 'paused', 'failed', 'ok', 'never_run'] as const;

export const integrationStreamSchema = z.object({
  stream: integrationStreamNameSchema,
  enabled: z.boolean(),
  status: z.enum(STREAM_STATUSES),
  lastSuccessAt: z.string().nullable(),
  /** Null when the stream only runs on demand. */
  nextDueAt: z.string().nullable(),
  lastError: z
    .object({
      /** Registered EHR error code (ADR-251), when the failure came from the EHR. */
      code: z.string().nullable(),
      message: z.string().nullable(),
      at: z.string(),
    })
    .nullable(),
  lastRun: z
    .object({
      id: z.string(),
      status: z.enum(['pending', 'running', 'completed', 'failed']),
      startedAt: z.string().nullable(),
      completedAt: z.string().nullable(),
      counts: z.record(z.string(), z.number()),
    })
    .nullable(),
});
export type IntegrationStreamView = z.infer<typeof integrationStreamSchema>;

export const integrationStreamsResponseSchema = z.object({
  streams: z.array(integrationStreamSchema),
});
export type IntegrationStreamsResponse = z.infer<typeof integrationStreamsResponseSchema>;

export const runStreamsRequestSchema = z.object({
  streams: z.array(integrationStreamNameSchema).min(1),
});
export type RunStreamsRequest = z.infer<typeof runStreamsRequestSchema>;

export const runStreamsResultSchema = z.object({
  results: z.array(
    z.object({
      stream: integrationStreamNameSchema,
      accepted: z.boolean(),
      /** Why a stream could not start now; null when accepted. */
      reason: z.string().nullable(),
    })
  ),
});
export type RunStreamsResult = z.infer<typeof runStreamsResultSchema>;

export const updateStreamRequestSchema = z.object({ enabled: z.boolean() });
export type UpdateStreamRequest = z.infer<typeof updateStreamRequestSchema>;

export const CHANGE_FEED_NAMES = ['appointments', 'patients'] as const;
export const changeFeedNameSchema = z.enum(CHANGE_FEED_NAMES);
export type ChangeFeedName = z.infer<typeof changeFeedNameSchema>;

/**
 *   - `unchecked` — never read from the EHR.
 *   - `converged` — the EHR reports what we want.
 *   - `pending`   — a change was requested and the EHR hasn't applied it yet
 *                   (athena: 10–20 minutes).
 *   - `drifted`   — the EHR disagrees and nothing is pending; the reconciler asks again.
 */
export const SUBSCRIPTION_STATES = ['unchecked', 'converged', 'pending', 'drifted'] as const;

export const integrationSubscriptionSchema = z.object({
  feed: changeFeedNameSchema,
  desired: z.enum(['subscribed', 'unsubscribed']),
  observed: z.enum(['active', 'partial', 'inactive', 'unknown']).nullable(),
  events: z.array(z.string()),
  observedAt: z.string().nullable(),
  requested: z.enum(['subscribe', 'unsubscribe']).nullable(),
  requestedAt: z.string().nullable(),
  state: z.enum(SUBSCRIPTION_STATES),
  /**
   * Until when the API refuses to flip `desired` (409), because the last request
   * may still be landing (ADR-299 Gotcha 14). Null when a change is allowed now.
   */
  changeLockedUntil: z.string().nullable(),
  error: z.object({ code: z.string().nullable(), message: z.string() }).nullable(),
});
export type IntegrationSubscriptionView = z.infer<typeof integrationSubscriptionSchema>;

export const integrationSubscriptionsResponseSchema = z.object({
  subscriptions: z.array(integrationSubscriptionSchema),
});
export type IntegrationSubscriptionsResponse = z.infer<
  typeof integrationSubscriptionsResponseSchema
>;

export const setSubscriptionRequestSchema = z.object({
  desired: z.enum(['subscribed', 'unsubscribed']),
});
export type SetSubscriptionRequest = z.infer<typeof setSubscriptionRequestSchema>;
